import {
  compressChartConfig,
  decompressChartConfig,
} from '@/features/chart/utils/chart-config-compression'

const MAIN_SERIES_ID = '_seriesId'

// TradingView re-mints study and price-scale ids on every layout load and re-derives
// the bar spacing from the container width (sub-percent drift), so a restored chart
// never byte-matches its own saved content. Canonicalise ids by order of appearance
// and bucket the spacing to ~10% steps: drift stays inside a bucket, a zoom crosses one.
const BAR_SPACING_KEY = 'm_barSpacing'
const bucketBarSpacing = (spacing: number) => Math.round(Math.log(spacing) * 10)

function collectIds(value: unknown, ids: Map<string, string>): void {
  if (Array.isArray(value)) {
    value.forEach((item) => collectIds(item, ids))
    return
  }
  if (!value || typeof value !== 'object') return
  const record = value as Record<string, unknown>
  const id = record.id
  if (typeof id === 'string' && id !== MAIN_SERIES_ID && !ids.has(id)) {
    ids.set(id, `$id${ids.size}`)
  }
  Object.values(record).forEach((item) => collectIds(item, ids))
}

function canonicalize(value: unknown, ids: Map<string, string>): unknown {
  if (typeof value === 'string') return ids.get(value) ?? value
  if (Array.isArray(value)) return value.map((item) => canonicalize(item, ids))
  if (!value || typeof value !== 'object') return value
  const next: Record<string, unknown> = {}
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    next[ids.get(key) ?? key] =
      key === BAR_SPACING_KEY && typeof item === 'number'
        ? bucketBarSpacing(item)
        : canonicalize(item, ids)
  }
  return next
}

export function normalizeChartState(state: unknown): unknown {
  const ids = new Map<string, string>()
  collectIds(state, ids)
  return canonicalize(state, ids)
}

// The key map collides with raw TradingView keys (`inputs[].min`, `layoutsSizes.s`),
// so a save/restore round trip changes the state. Both sides go through it: restored
// content already has, an in-session save has not.
const roundTrip = (state: unknown) => decompressChartConfig(compressChartConfig(state))

/** True when `state` differs from `savedContent` only by TradingView-generated noise. */
export function isChartStateUnchanged(state: unknown, savedContent: string | undefined): boolean {
  if (!savedContent) return false
  let saved: unknown
  try {
    saved = JSON.parse(savedContent)
  } catch {
    return false
  }
  const key = (value: unknown) => JSON.stringify(normalizeChartState(roundTrip(value)))
  return key(state) === key(saved)
}
